"use client";
import {
  setCountBookmark,
  setPageShareArray,
  setPageShareCurrent,
} from "@lib/features/reportSlice";
import { useAppDispatch } from "@lib/hooks";
import { Box } from "@mui/material";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReportHeader from "./ReportHeader";
import ReportLeft from "./ReportLeft";
import ReportRight from "./ReportRight";
// import { useQuery } from "@tanstack/react-query";
import { ConfigData } from "@/models/config";
import { User } from "@/models/user";
import ReportServices from "@/services/main/reportServices"; // ✅ Thêm service
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"; // ✅ Thêm useMutation, useQueryClient
import { AnimatePresence } from "framer-motion"; // ✅ Import AnimatePresence
import { useTranslations } from "next-intl";
import BookmarkReview from "./BookmarkReview";
import BookmarkReviewMobile from "./BookmarkReviewMobile"; // ✅ Import BookmarkReviewMobile
const PopupShareContent = dynamic(
  () =>
    import(
      "@/app/[locale]/(user-page)/report/[slug]/components/PopupShareContent"
    ),
  {
    ssr: false,
  }
);

interface ReportContentProps {
  reportAttachments?: any;
  reportId: number;
  dataDetailType?: number;
  isPaymentEd?: boolean;
  isShare?: boolean;
  slug?: string;
  isOpenProtected?: boolean;
  dataDetail?: any;
  reportAttachmentsOption?: string[];
  lang: string;
  user?: User | null;
  isMobile?: boolean;
}

export interface SidebarState {
  selectedPage: number;
  sidebarVisible: boolean;
}

interface ReportContentState {
  totalPages?: number;
  initialBookmarkOpen?: boolean;
  isFullscreen?: boolean;
  bookmarkReviewVisible?: boolean;
}

const ReportContent = (props: ReportContentProps) => {
  const t = useTranslations("messages");
  const queryClient = useQueryClient();
  const config = queryClient.getQueryData<ConfigData>(["config"]);
  const user = queryClient.getQueryData<User>(["user"]);
  const isLogin = !!user;
  const isJumpingRef = useRef<boolean>(false);
  const reportContainerRef = useRef<HTMLDivElement>(null);
  const [openShare, setOpenShare] = useState<boolean>(false);
  const leftJumpLockRef = useRef<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const rightRef = useRef<{
    zoomIn: () => void;
    zoomOut: () => void;
    resetZoom: () => void;
    wheelZoom: (e: WheelEvent) => void;
  }>(null);
  const dispatch = useAppDispatch();
  const [reportContentState, setReportContentState] =
    useState<ReportContentState>({
      totalPages: props.isShare
        ? (props.reportAttachments?.imagePreviews?.length ??
          props.reportAttachments?.pages)
        : props.reportAttachments?.pages,
      initialBookmarkOpen: false,
      isFullscreen: false,
      bookmarkReviewVisible: false,
    });
  const [sidebarState, setSidebarState] = useState<SidebarState>({
    selectedPage: 1,
    sidebarVisible: props.isMobile ? false : true, // Mặc định sidebar sẽ hiển thị trên desktop
  });

  const modalNewRef = useRef<{
    openModalNew: () => void;
    closeModalNew: () => void;
  } | null>(null);

  const pageRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setReportContentState((prevState) => ({
        ...prevState,
        isFullscreen: !!document.fullscreenElement,
      }));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Xử lý nút sidebar
  const toggleSidebar = () => {
    setSidebarState((prevState) => ({
      ...prevState,
      sidebarVisible: !prevState.sidebarVisible,
    }));
  };

  // Xử lý fullscreen
  const toggleFullscreen = () => {
    if (reportContainerRef.current) {
      if (!reportContentState.isFullscreen) {
        reportContainerRef.current?.requestFullscreen().catch((err) => {
          console.error(
            `Error attempting to enable full-screen mode: ${err.message}`
          );
        });
      } else {
        if (document.fullscreenElement) {
          // Nếu đang ở chế độ fullscreen, thoát khỏi chế độ này
          document.exitFullscreen();
        }
      }
    }
  };

  const previews = useMemo(() => {
    return props.reportAttachments ? props.reportAttachments?.pdfPreviews : [];
  }, [props.reportAttachments]);

  const pages = useMemo(
    () =>
      props?.isShare
        ? previews?.map((item: any, index: number) => ({
            id: index + 1,
            page: item.page,
            title: t("report_left_page_caption", { number: item.page }),
            thumbnailUrl: item?.image || null,
          }))
        : previews?.map((item: any, index: number) => {
            return {
              id: index + 1,
              page: item.page,
              title: t("report_left_page_caption", { number: item.page }),
              thumbnailUrl: item?.image || null,
            };
          }),
    [previews, props?.isShare, t]
  );

  const handlePageSelect = useCallback(
    (pageId: number) => {
      // prevent double-trigger while a left-initiated jump is in-progress
      if (leftJumpLockRef.current && pageId === currentPage) return;

      // mark jumping from left, lock parent-visible updates briefly
      isJumpingRef.current = true;
      leftJumpLockRef.current = true;

      setCurrentPage(pageId);

      // Keep locks for estimated duration (matches ReportRight fallback window)
      const baseTime = 500;
      const extraTime = Math.min(pages.length * 10, 5000);
      const totalTime = baseTime + extraTime;

      window.setTimeout(() => {
        if (isJumpingRef.current) isJumpingRef.current = false;
        leftJumpLockRef.current = false;
      }, totalTime + 150); // small extra buffer
    },
    [pages, currentPage]
  );

  const handleVisiblePageChange = useCallback(
    (pageId: number) => {
      if (leftJumpLockRef.current && pageId !== currentPage) {
        return;
      }
      setCurrentPage(pageId);
    },
    [currentPage, setCurrentPage]
  );

  // Xử lý bookmark review
  const toggleBookmarkReview = () => {
    setReportContentState((prevState) => ({
      ...prevState,
      bookmarkReviewVisible: !prevState.bookmarkReviewVisible,
    }));
  };

  // ================== NÂNG CẤP LOGIC TỪ ReportRight LÊN ĐÂY ==================

  // 1. Lấy dữ liệu bookmarks
  const { data: dataBookmarks, isLoading: isLoadingBookmarks } = useQuery({
    queryKey: ["bookmarks", props.reportId, props.lang],
    queryFn: async () => {
      const result = await ReportServices.getReportBookmarks({
        reportId: props.reportId,
        language: props.lang,
      });
      // Logic dispatch có thể giữ lại hoặc bỏ đi tùy vào nhu cầu
      dispatch(
        setCountBookmark({
          [props.reportId]: result?.data?.length || 0,
        })
      );
      return result?.data;
    },
    refetchOnWindowFocus: false,
    enabled: !!isLogin,
  });

  // 2. Logic tính toán và kiểm tra giới hạn share
  const handleCalculatePercentage = useMemo((): number => {
    const pageCount = pages?.length;
    const percentage = Math.round(
      Number(pageCount) * (Number(config?.shareLimitPage || 0) / 100)
    );
    if (pageCount >= 0 && pageCount < 10) {
      return 1;
    }
    return percentage;
  }, [pages, config?.shareLimitPage]);

  const mutationCheckLimitShare = useMutation({
    // ... (Giữ nguyên logic của mutation này từ ReportRight)
    mutationKey: ["check-limit-share", props.reportId],
    mutationFn: async (data: {
      reportId: number | string;
      page: number | null;
      pages: number[] | null;
    }) => {
      const check = await ReportServices.checkLimitShare(data.reportId);
      return { ...check, page: data.page, pages: data.pages };
    },
    onSuccess: (res: any) => {
      if (res?.data?.accept) {
        handleOpenShare(res?.page, res?.pages);
      } else {
        // Cần có hàm handleClickOpenWarnning ở đây nếu muốn dùng dialog
        console.warn("Share limit exceeded");
      }
    },
  });

  const handleMutationCheckLimitShare = (
    reportId: number | string,
    page: number | null,
    pages: number[] | null
  ) => {
    mutationCheckLimitShare.mutate({ reportId, page, pages });
  };
  // ================== KẾT THÚC NÂNG CẤP LOGIC ==================

  const handleOpenShare = (
    page?: number | null,
    pages: number[] | null = null
  ) => {
    if (pages && pages?.length > 0) {
      dispatch(setPageShareArray(pages));
      modalNewRef.current?.openModalNew();
      return;
    }
    if (page) {
      dispatch(setPageShareCurrent(page));
      modalNewRef.current?.openModalNew();
    } else {
      setOpenShare(true);
    }
  };
  const handleOpenModalShareList = () => {
    setOpenShare(true);
  };

  /** DAT: EFFECT MỚI: Tự động tính toán tỉ lệ khung hình cho thumbnail
   * Mục đích là để tránh việc phải tính toán lại mỗi lần render
   */
  const [cachedAspectRatio, setCachedAspectRatio] = useState<string | null>(
    null
  );

  // EFFECT MỚI: Tự động tính toán khi `pages` thay đổi
  useEffect(() => {
    // Nếu không có trang nào, hoặc đã có cache rồi thì không cần tính lại
    if (!pages || pages.length === 0) {
      return;
    }
    // Reset cache khi có bộ dữ liệu `pages` mới
    setCachedAspectRatio(null);
    const firstPage = pages[0];
    if (firstPage && firstPage.thumbnailUrl) {
      // Flag kiểm tra component tồn tại
      let isMounted = true;
      // Tạo một đối tượng Image trong bộ nhớ, không render ra DOM
      const img = new window.Image();
      // Gán hàm xử lý sự kiện onload
      img.onload = () => {
        if (!isMounted) return;
        // Khi ảnh đã tải xong, tính toán và cập nhật state cache
        const aspectRatio = `${img.naturalWidth} / ${img.naturalHeight}`;
        // console.log("Pre-calculated Aspect Ratio:", aspectRatio);
        setCachedAspectRatio(aspectRatio);
      };
      // Gán nguồn ảnh để bắt đầu quá trình tải
      img.src = firstPage.thumbnailUrl;
      // Cleanup function:
      return () => {
        isMounted = false; // Đánh dấu là đã unmount
        img.onload = null; // Gỡ bỏ event handler để tránh gọi setState trên component đã unmount
      };
    }
  }, [pages]);
  /**END EFFECT MỚI */

  return props.isOpenProtected ? null : (
    <div
      id={"report-container"}
      ref={reportContainerRef}
      className={`relative !overflow-visible report-container ${reportContentState.isFullscreen ? "bg-white" : ""}`}
    >
      {!reportContentState.isFullscreen && (
        <Box className="sticky top-0 z-20 left-0 w-full bg-white">
          <ReportHeader
            user={props.user}
            lang={props.lang}
            reportAttachmentsOption={props.reportAttachmentsOption}
            rightRef={rightRef}
            isPayment={props.isPaymentEd}
            isShare={props.isShare}
            slug={props.slug}
            reportContentState={reportContentState}
            isLogin={isLogin}
            reportId={props.reportId}
            reportAttachments={props.reportAttachments}
            currentPage={currentPage}
            totalPages={reportContentState.totalPages}
            onToolbarToggle={toggleSidebar}
            onFullscreen={toggleFullscreen}
            onShare={handleOpenShare}
            reportType={props.dataDetailType}
            onBookmark={toggleBookmarkReview}
            previews={previews}
            isFullscreen={reportContentState.isFullscreen}
            // zoomPercentage={zoomPercentage}
            // showZoomIndicator={showZoomIndicator}
          />
          {/* NÂNG CẤP GIAO DIỆN: RENDER BOOKMARK REVIEW TẠI ĐÂY */}
          <AnimatePresence>
            {reportContentState.bookmarkReviewVisible &&
              (props.isMobile ? (
                <BookmarkReviewMobile
                  locale={props.lang}
                  sidebarState={sidebarState}
                  handleCalculatePercentage={handleCalculatePercentage}
                  handleMutationCheckLimitShare={handleMutationCheckLimitShare}
                  reportId={props.reportId}
                  isLoading={isLoadingBookmarks}
                  data={dataBookmarks}
                  pages={pages}
                  selectedPage={currentPage}
                  onPageSelect={handlePageSelect}
                />
              ) : (
                <BookmarkReview
                  locale={props.lang}
                  data={dataBookmarks}
                  isLoading={isLoadingBookmarks}
                  handleCalculatePercentage={handleCalculatePercentage}
                  handleMutationCheckLimitShare={handleMutationCheckLimitShare}
                  reportId={props.reportId}
                  pages={pages}
                  selectedPage={currentPage}
                  onPageSelect={handlePageSelect}
                  sidebarState={sidebarState}
                />
              ))}
          </AnimatePresence>
        </Box>
      )}
      <Box
        className={`report-body flex flex-col md:flex-row ${props.isMobile ? "h-[100vh]" : ""}`}
      >
        {!reportContentState.isFullscreen && sidebarState.sidebarVisible && (
          <ReportLeft
            isJumpingRef={isJumpingRef}
            isMobile={props.isMobile}
            isOpen={sidebarState.sidebarVisible}
            pages={pages}
            selectedPage={currentPage}
            onPageSelect={handlePageSelect}
            reportAttachments={props.reportAttachments}
            isLogin={isLogin}
            reportType={props.dataDetailType}
            isPaymentEd={props.isPaymentEd}
            currentPage={currentPage}
            aspectRatio={cachedAspectRatio}
          />
        )}
        {/* Right content - takes remaining space */}
        <ReportRight
          ref={rightRef}
          isJumpingRef={isJumpingRef}
          slug={props.slug}
          isShare={props.isShare}
          reportId={props.reportId}
          user={props.user}
          reportContainerRef={reportContainerRef}
          setSidebarState={setSidebarState}
          pages={pages}
          onShare={handleOpenShare}
          isFullscreen={reportContentState.isFullscreen}
          onExitFullscreen={toggleFullscreen}
          onPageSelect={handlePageSelect}
          currentPage={currentPage}
          onVisiblePageChange={handleVisiblePageChange}
          isLogin={isLogin}
          reportType={props.dataDetailType}
          isPaymentEd={props.isPaymentEd}
          pageRefs={pageRefs}
          dataDetail={props.dataDetail}
          locale={props.lang}
        />
      </Box>
      <Box className="!card-glass-effect-2 !bg-opacity-0"></Box>
      <PopupShareContent
        typeReport={props.dataDetailType}
        ref={modalNewRef}
        onClose={() => setOpenShare(false)}
        handleOpenModalShareList={handleOpenModalShareList}
        isOpen={openShare}
        reportId={props.reportId}
        isPaymentEd={props.isPaymentEd}
        pages={props.reportAttachments?.page}
        pdfPreviews={props.reportAttachments?.pdfPreviews}
      />
    </div>
  );
};

export default ReportContent;
